import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Redirect, Route, Switch} from 'react-router';
import config from 'config';
import './index.css';

import {AuthComponent, Permissions} from 'components/ComponentSecurity';

import {addNotification as addNotificationAction} from 'services/notification/action';
import Header from './components/header';
import SideNav from './components/side-bar/SideNav';
import ChevronLeftIcon from "../../assets/icons/ChevronLeftIcon";
import ChevronRightIcon from "../../assets/icons/ChevronRightIcon";
import Index from './components/management/index';
import Chat from './components/chat';
import Graph from './components/graph/Graph';
import {fetchCompanyRequested} from '../../services/company/action';
import {getUserDataRequested, getUserSystemNotificationsRequested} from '../../services/user/action';
import {initChatRequested} from '../../services/chat/action';
import LoadingComponent from '../../components-new/ui/LoadingComponent';
import Billing from "./components/billing";
import MyHistory from "./components/my-history/MySpace";
import InternalGrouping from "./components/internal-grouping/InternalGrouping";
import Home from "./components/home";
import Translate from '../../components/Translate';

const SIDEBAR_OPEN_STORAGE_KEY = 'dashboardSidebarOpen';

const getPageTitleKey = (pathname) => {
    const titledRoutes = [
        {path: '/dashboard/management/history', titleKey: 'history'},
        {path: '/dashboard/management/statistics', titleKey: 'statistics'},
        {path: '/dashboard/management/members', titleKey: 'members'},
        {path: '/dashboard/management/clients', titleKey: 'Bots'},
        {path: '/dashboard/home', titleKey: 'dashboardTitle'},
        {path: '/dashboard/my/space', titleKey: 'My Space'},
        {path: '/dashboard/internal-groups', titleKey: 'Internal Groups'},
        {path: '/dashboard/billing', titleKey: 'Billing'},
        {path: '/dashboard/graph/', titleKey: 'graphs'},
    ];

    const activeRoute = titledRoutes.find(route => pathname.indexOf(route.path) === 0);
    return activeRoute ? activeRoute.titleKey : null;
};

const getInitialSidebarOpen = () => {
    if (typeof window === 'undefined') {
        return true;
    }

    if (window.matchMedia('(max-width: 768px)').matches) {
        return false;
    }

    return window.localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY) !== 'false';
};

class Dashboard extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            sidebarOpen: getInitialSidebarOpen(),
            mobileSidebarOpen: false,
        };

        this.toggleSidebar = this.toggleSidebar.bind(this);
        this.closeMobileSidebar = this.closeMobileSidebar.bind(this);
        this.handleSidebarControlClick = this.handleSidebarControlClick.bind(this);
        this.chatInitializationRequested = false;
    }

    componentDidMount() {
        const {fetchCompany, getUserData, user, getUserSystemNotifications} = this.props;
        if (user.isAuthenticated) {
            fetchCompany();
            getUserData();
            getUserSystemNotifications();
        }
        this.initializeChatWhenReady();
    }

    componentDidUpdate() {
        this.initializeChatWhenReady();
    }

    initializeChatWhenReady = () => {
        const {user, company, initChat} = this.props;
        if (this.chatInitializationRequested || !user.isAuthenticated ||
            !user.initialInfoFetched || !company.initialInfoFetched) return;

        const status = company.tariff && company.tariff.status;
        if (status !== config.tariffStatusMap.active && status !== config.tariffStatusMap.trial) return;

        this.chatInitializationRequested = true;
        initChat();
    };

    toggleSidebar() {
        this.setState((prevState) => {
            const sidebarOpen = !prevState.sidebarOpen;

            if (typeof window !== 'undefined') {
                window.localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, sidebarOpen ? 'true' : 'false');
            }

            return {sidebarOpen};
        });
    }

    closeMobileSidebar() {
        this.setState({mobileSidebarOpen: false});
    }

    handleSidebarControlClick() {
        // on a phone the panel is a drawer that is either over the page or off
        // it, so the same control has to close it as well as open it
        if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
            this.setState(prevState => ({mobileSidebarOpen: !prevState.mobileSidebarOpen}));
            return;
        }

        this.toggleSidebar();
    }

    render() {
        const {
            user, match, location, company, addNotification
        } = this.props;
        const {sidebarOpen, mobileSidebarOpen} = this.state;

        const {tariff} = company;
        const {features} = company.tariff;

        let matchUrl = match.url;
        if (matchUrl === '/') {
            matchUrl = '/dashboard';
        }

        if (!user.isAuthenticated) {
            window.location.href = config.APP_HOST + "/login"
        }

        if (!user.initialInfoFetched || !company.initialInfoFetched) {
            return <LoadingComponent/>;
        }

        const defaultProps = {
            user,
            addNotification,
            company,
        };
        const pageTitleKey = getPageTitleKey(location.pathname);
        const isChatPage = location.pathname.includes('/chat/');

        return (
            <div
                className={`dashboard full-height ${sidebarOpen ? 'dashboard-sidebar-open' : 'dashboard-sidebar-collapsed'} ${mobileSidebarOpen ? 'dashboard-sidebar-mobile-open' : ''}`}>
                <Header
                    user={user}
                    company={company}
                    mobileSidebarOpen={mobileSidebarOpen}
                    onToggleMobileSidebar={this.handleSidebarControlClick}
                />

                {mobileSidebarOpen && (
                    <button
                        type="button"
                        className="dashboard-sidebar-backdrop"
                        aria-label="Close navigation"
                        onClick={this.closeMobileSidebar}
                    />
                )}
                <div className="dashboard-body">
                    <Permissions
                        user={user}
                        component={SideNav}
                        company={company}
                        collapsed={!sidebarOpen}
                        mobileOpen={mobileSidebarOpen}
                        onCloseMobile={this.closeMobileSidebar}
                    />
                    {/* Immediately follows the sidebar so its visibility can
                        be driven by sidebar hover without browser-specific
                        parent selectors. It remains outside the sidebar itself
                        because that panel clips horizontal overflow. */}
                    <button
                        type="button"
                        className="dashboard-sidebar-edge-toggle"
                        onClick={this.handleSidebarControlClick}
                        aria-label={sidebarOpen ? 'Collapse navigation' : 'Expand navigation'}
                    >
                        {sidebarOpen
                            ? <ChevronLeftIcon width="14" height="14" strokeWidth={2}/>
                            : <ChevronRightIcon width="14" height="14" strokeWidth={2}/>}
                    </button>
                    <main className={`full-height dashboard-main${isChatPage ? ' dashboard-main--chat' : ''}`}>
                        {pageTitleKey && (
                            <div className="dashboard-main-title">
                                <Translate text={pageTitleKey}/>
                            </div>
                        )}
                        <div className={`dashboard-main-content${isChatPage ? ' dashboard-main-content--chat' : ''}`}>
                        <Switch>
                        {(tariff.status === config.tariffStatusMap.active || tariff.status === config.tariffStatusMap.trial) &&
                            <Route
                                path={`${matchUrl}/chat/:id`}
                                render={props => (
                                    <AuthComponent {...defaultProps} requiredPermission={config.permissions.any}
                                                   component={Chat} {...props} />)}
                            />
                        }

                        {(tariff.status === config.tariffStatusMap.active || tariff.status === config.tariffStatusMap.trial) &&
                            (features.includes(config.features.MY_HISTORY) || features.includes(config.features.TICKETS) || features.includes(config.features.TEMPLATES) || features.includes(config.features.HOTKEYS)) &&
                            <Route
                                path={`${matchUrl}/my/space`}
                                render={props => (
                                    <AuthComponent {...defaultProps} requiredPermission={config.permissions.any}
                                                   component={MyHistory} {...props} />)}
                            />
                        }

                        {(tariff.status === config.tariffStatusMap.active || tariff.status === config.tariffStatusMap.trial) &&
                            features.includes(config.features.INTERNAL_GROUPING) &&
                            <Route path={`${matchUrl}/internal-groups`}
                                   render={props => (
                                       <AuthComponent
                                           {...defaultProps}
                                           requiredPermission={config.permissions.management}
                                           component={InternalGrouping}
                                           {...props}
                                       />
                                   )}
                            />
                        }

                        {features.includes(config.features.BILLING) && (
                            <Route
                                path={`${matchUrl}/billing`}
                                render={props => (
                                    <AuthComponent {...defaultProps} requiredPermission={config.permissions.billing}
                                                   component={Billing} {...props} />)}
                            />
                        )}
                        {(tariff.status === config.tariffStatusMap.active
                                || tariff.status === config.tariffStatusMap.trial
                                || tariff.status === config.tariffStatusMap.paymentRequired) &&
                            <Route
                                path={`${matchUrl}/management`}
                                render={props => (
                                    <AuthComponent {...defaultProps} requiredPermission={config.permissions.management}
                                                   component={Index} {...props} />)}
                            />
                        }
                        {(tariff.status === config.tariffStatusMap.active || tariff.status === config.tariffStatusMap.trial) &&
                            <Route path={`${matchUrl}/graph/:clientId`}
                                   render={props => (<AuthComponent {...defaultProps} component={Graph}
                                                                    requiredPermission={config.permissions.any}
                                                                    user={user}
                                                                    tariff={tariff} {...props} />)}/>
                        }
                        {(tariff.status === config.tariffStatusMap.active || tariff.status === config.tariffStatusMap.trial) &&
                            <Route
                                path={`${matchUrl}/home`}
                                render={props => (
                                    <AuthComponent {...defaultProps} requiredPermission={config.permissions.any}
                                                   component={Home} {...props} />)}
                            />
                        }
                        {/* The landing. It used to point straight at the internal
                            chats, for every role and whether or not the company
                            had that feature at all. Home decides for itself what
                            to show, and sends the few companies with no statistics
                            on to the chats. */}
                        {(tariff.status === config.tariffStatusMap.active || tariff.status === config.tariffStatusMap.trial) &&
                            <Route path={`${matchUrl}/`}
                                   render={() => (<Redirect to={`${matchUrl}/home`}/>)}/>
                        }
                        </Switch>
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

Dashboard.propTypes = {
    initChat: PropTypes.func.isRequired,
    user: PropTypes.shape({
        isAuthenticated: PropTypes.bool.isRequired,
    }).isRequired,
    company: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    location: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    fetchCompany: PropTypes.func.isRequired,
    getUserData: PropTypes.func.isRequired,
    getUserSystemNotifications: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
    user: state.user,
    company: state.company,
});

const mapDispatchToProps = dispatch => ({
    initChat: () => dispatch(initChatRequested()),
    fetchCompany: () => {
        dispatch(fetchCompanyRequested());
    },
    getUserData: () => {
        dispatch(getUserDataRequested());
    },
    getUserSystemNotifications: () => {
        dispatch(getUserSystemNotificationsRequested());
    },
    addNotification: (message, level, notificationConfig) => {
        dispatch(addNotificationAction(message, level, notificationConfig));
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/index.jsx